import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Shield, Users, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#000000' }}>Terms of Service</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="mb-6" style={{ color: '#333333' }}>
              <strong>Effective Date:</strong> October 15, 2025<br />
              <strong>Last Updated:</strong> October 15, 2025
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>1. Acceptance of Terms</h2>
            <p className="mb-6" style={{ color: '#000000' }}>
              By accessing or using Quixocial ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>2. Description of Service</h2>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>Quixocial is a social media management platform that allows users to:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-semibold" style={{ color: '#000000' }}>Content Creation</h4>
                    <p className="text-sm" style={{ color: '#333333' }}>Create and manage social media content</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-purple-600 mt-1" />
                  <div>
                    <h4 className="font-semibold" style={{ color: '#000000' }}>AI-Powered Tools</h4>
                    <p className="text-sm" style={{ color: '#333333' }}>Generate content using Google Gemini</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h4 className="font-semibold" style={{ color: '#000000' }}>Team Collaboration</h4>
                    <p className="text-sm" style={{ color: '#333333' }}>Collaborate with team members</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-orange-600 mt-1" />
                  <div>
                    <h4 className="font-semibold" style={{ color: '#000000' }}>Analytics</h4>
                    <p className="text-sm" style={{ color: '#333333' }}>Analyze performance and engagement</p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>3. User Accounts</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#000000' }}>3.1 Account Creation</h3>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <ul className="list-disc list-inside space-y-1" style={{ color: '#000000' }}>
                <li>You must provide accurate and complete information</li>
                <li>You are responsible for maintaining account security</li>
                <li>You must be at least 18 years old to use the Service</li>
                <li>One person or legal entity may maintain only one account</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#000000' }}>3.2 Account Responsibilities</h3>
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
              <ul className="list-disc list-inside space-y-1" style={{ color: '#000000' }}>
                <li>You are responsible for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
                <li>We reserve the right to suspend accounts that violate these Terms</li>
              </ul>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>4. Acceptable Use</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#000000' }}>✅ Permitted Uses</h3>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Create original content for business</li>
                  <li>Generate AI-powered content and captions</li>
                  <li>Schedule and publish content</li>
                  <li>Analyze performance metrics</li>
                  <li>Collaborate with team members</li>
                  <li>Store and manage brand assets</li>
                  <li>Create and manage brand kits and visual assets</li>
                  <li>Use AI for content optimization and recommendations</li>
                  <li>Connect and manage multiple social media accounts</li>
                  <li>Access analytics and performance insights</li>
                  <li>Use automation features for posting and scheduling</li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#000000' }}>❌ Prohibited Uses</h3>
                <ul className="list-disc list-inside text-sm space-y-1" style={{ color: '#000000' }}>
                  <li>Violate any applicable laws</li>
                  <li>Infringe on intellectual property</li>
                  <li>Post spam or malicious content</li>
                  <li>Attempt unauthorized access</li>
                  <li>Engage in harassment or abuse</li>
                  <li>Generate inappropriate, offensive, or harmful content</li>
                  <li>Misuse AI features to create misleading or false information</li>
                  <li>Violate social media platform terms of service</li>
                  <li>Share account credentials with unauthorized users</li>
                  <li>Use the service to impersonate others or create fake accounts</li>
                  <li>Attempt to reverse engineer or copy AI models</li>
                  <li>Exceed usage limits or abuse system resources</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>5. Content and Intellectual Property</h2>
            
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#000000' }}>Your Content</h3>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#000000' }}>
                <li>You retain ownership of content you create and upload</li>
                <li>You grant us a limited license to use your content to provide the Service</li>
                <li>You are responsible for ensuring you have rights to all content</li>
                <li>You warrant that your content does not violate third-party rights</li>
              </ul>
              
              <div className="mt-4 p-4 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg">
                <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>Content Responsibility</h4>
                <p style={{ color: '#000000' }}>
                  Users are solely responsible for the content they create, schedule, or publish using Quixocial, including text, images, videos, and AI-generated content. Quixocial does not post content on behalf of users without their explicit action or authorization.
                </p>
              </div>
            </div>

            <div className="bg-purple-50 p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#000000' }}>AI-Generated Content</h3>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#000000' }}>
                <li>Content generated using our AI features is provided "as is"</li>
                <li>You are responsible for reviewing all AI-generated content before publication</li>
                <li>We do not guarantee the accuracy or quality of AI-generated content</li>
              </ul>
              
              <div className="mt-4 p-4 bg-purple-100 border-l-4 border-purple-500 rounded-r-lg">
                <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>AI Content Review Required</h4>
                <p style={{ color: '#000000' }}>
                  Content generated using Quixocial AI tools must be reviewed and approved by the user before publication. Users are responsible for ensuring AI-generated content complies with applicable laws, social media platform policies, and intellectual property rights.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>6. Social Media Platform Integration</h2>
            <div className="bg-gray-100 p-6 rounded-lg mb-6">
              <p className="mb-4" style={{ color: '#000000' }}>
                The Service integrates with Facebook, Instagram, X, YouTube, Pinterest, and other platforms:
              </p>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#000000' }}>
                <li>You must comply with the terms of service of all connected platforms</li>
                <li>We are not responsible for changes to third-party platform APIs or policies</li>
                <li>You authorize us to publish content to your connected social media accounts</li>
                <li>You are solely responsible for the content published through the Service</li>
              </ul>
              
              <div className="mt-4 p-4 bg-gray-200 border-l-4 border-gray-500 rounded-r-lg">
                <h4 className="font-semibold mb-2" style={{ color: '#000000' }}>OAuth Authorization</h4>
                <p style={{ color: '#000000' }}>
                  Users must authorize Quixocial to access their social media accounts via OAuth. Quixocial will only use these permissions to provide the requested services, including scheduling, publishing, and analyzing content. Access tokens are securely stored and not used for any other purpose.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>7. Limitation of Liability</h2>
            <div className="bg-gray-900 text-white p-6 rounded-lg mb-6">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#ffffff' }}>Disclaimer of Warranties</h3>
              <ul className="list-disc list-inside space-y-2" style={{ color: '#ffffff' }}>
                <li>The Service is provided "as is" without warranties of any kind</li>
                <li>We disclaim all warranties, express or implied</li>
                <li>We do not warrant that the Service will be error-free or uninterrupted</li>
                <li>Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim</li>
              </ul>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#000000' }}>8. Contact Information</h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="mb-2" style={{ color: '#000000' }}>
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-1" style={{ color: '#000000' }}>
                <p><strong>Email:</strong> info@quixocial.com</p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-center" style={{ color: '#000000' }}>
                By using Quixocial, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;